import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Heart,
  Send,
  Search,
  Image,
  Video,
  X,
  Check,
  ThumbsUp,
  MessageCircle,
  ArrowLeft,
  FileText,
} from 'lucide-react';
import { Avatar } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { usersApi, appreciationsApi } from '../../services/api';
import { User, CompanyValue, MediaFile } from '../../types';
import { renderValueIcon } from '../../utils/iconMap';
import MentionInput, { getDisplayLength } from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionText/MentionText';
import styles from './Appreciate.module.css';

const RIPPLE_TYPES = [
  { id: 'kudos', label: 'Kudos', waves: 10, emoji: '👏', color: '#8B5CF6', bg: '#F5F3FF', border: '#DDD6FE', swatch: '#c4b5fd' },
  { id: 'shoutout', label: 'Shoutout', waves: 15, emoji: '📣', color: '#EC4899', bg: '#FDF2F8', border: '#FBCFE8', swatch: '#f9a8d4' },
  { id: 'thank_you', label: 'Thank You', waves: 10, emoji: '🙏', color: '#6366F1', bg: '#EEF2FF', border: '#C7D2FE', swatch: '#a5b4fc' },
  { id: 'great_job', label: 'Great Job', waves: 20, emoji: '🏆', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', swatch: '#fbbf24' },
  { id: 'team_player', label: 'Team Player', waves: 15, emoji: '🤝', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0', swatch: '#6ee7b7' },
  { id: 'innovator', label: 'Innovator', waves: 25, emoji: '💡', color: '#0EA5E9', bg: '#F0F9FF', border: '#BAE6FD', swatch: '#7dd3fc' },
  { id: 'customer_hero', label: 'Customer Hero', waves: 20, emoji: '❤️', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', swatch: '#fca5a5' },
];

const WAVE_PRESETS = [50, 100, 200, 500];

const Appreciate: React.FC = () => {
  const { user, refreshUser } = useAuthStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [values, setValues] = useState<CompanyValue[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [customPoints, setCustomPoints] = useState(100);
  const [isCustomWave, setIsCustomWave] = useState(false);
  const [isPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const fileDocInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const typeObj = RIPPLE_TYPES.find(t => t.id === selectedType);
  const waveCostPerPerson = customPoints || (typeObj?.waves || 0);
  const totalWaveCost = waveCostPerPerson * selectedUsers.length;
  const afterBalance = (user?.points_balance || 0) - totalWaveCost;
  const displayLen = getDisplayLength(message);
  const canSend = selectedUsers.length > 0 && selectedType && displayLen >= 10 && afterBalance >= 0;

  useEffect(() => {
    fetchUsers();
    appreciationsApi.getValues().then(res => setValues(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await usersApi.getAll({ page: 1, limit: 200 });
      setUsers(res.data.users.filter((u: User) => u.id !== user?.id && u.is_active));
    } catch { }
    setUsersLoading(false);
  };

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    setShowDropdown(true);
  };

  const filteredUsers = users.filter(u =>
    `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const suggestedUsers = users.filter(u => !selectedUsers.some(su => su.id === u.id)).slice(0, 4);

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    const remaining = 5 - mediaFiles.length;
    files.slice(0, remaining).forEach(file => {
      if (file.size > 5 * 1024 * 1024) { alert('File size must be less than 5MB'); return; }
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType = isImage ? 'image' : isVideo ? 'video' : 'file';
      setMediaFiles(prev => [...prev, { file, url: URL.createObjectURL(file), type: fileType as any, name: file.name }]);
    });
    e.target.value = '';
  };

  const handleRemoveMedia = (index: number) => {
    setMediaFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].url);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const toggleValue = (id: string) => {
    setSelectedValues(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSend || loading) return;
    setLoading(true);
    setError('');
    try {
      // Convert blob URLs to base64 data URIs for the server
      let uploadFiles: any[] | undefined;
      if (mediaFiles.length > 0) {
        uploadFiles = await Promise.all(
          mediaFiles.map((mf: any) => {
            if (!mf.file) return Promise.resolve({ url: mf.url, type: mf.type, name: mf.name });
            return new Promise<any>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve({ url: reader.result as string, type: mf.type, name: mf.name });
              reader.readAsDataURL(mf.file);
            });
          })
        );
      }

      const groupId = selectedUsers.length > 1 ? crypto.randomUUID() : undefined;
      for (const recipient of selectedUsers) {
        await appreciationsApi.create({
          to_user_id: recipient.id,
          type: selectedType!,
          message: message.trim(),
          points: waveCostPerPerson,
          company_value_id: [...selectedValues][0] || undefined,
          is_public: isPublic,
          media_files: uploadFiles,
          group_id: groupId,
        });
      }
      await refreshUser();
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send ripple');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.successContent}>
          <div className={styles.successIcon}><Check size={36} color="#fff" /></div>
          <h2>Ripple sent!</h2>
          <p>Your appreciation has been shared with {selectedUsers.map(u => u.first_name).join(' & ')}.</p>
          <div className={styles.successButtons}>
            <button onClick={() => { setSuccess(false); setSelectedUsers([]); setSelectedType(null); setMessage(''); setCustomPoints(100); setIsCustomWave(false); setMediaFiles([]); setSelectedValues(new Set()); setError(''); }} className={styles.sendAnotherBtn}>Send Another</button>
            <Link to="/feed" className={styles.viewFeedBtn}>View Spotlight</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Page header */}
      <div className={styles.pageHeaderRow}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </button>
      </div>
      <h1 className={styles.pageTitle}>Send a ripple</h1>
      <p className={styles.pageSubtitle}>Name what they did and why it mattered. Specific beats generous.</p>

      <div className={styles.formLayout}>
        {/* ═══ LEFT: FORM ═══ */}
        <div className={styles.formColumn}>

          {/* Step 1: Who */}
          <div className={`${styles.stepCard} ${selectedUsers.length > 0 ? styles.stepDone : ''}`} style={{ borderLeftColor: '#2f7fd1' }}>
            <div className={styles.stepHeader}>
              <span className={styles.stepNum} style={{ background: '#2f7fd1' }}>{selectedUsers.length > 0 ? <Check size={13} /> : '1'}</span>
              <h2 className={styles.stepTitle}>Who are you thanking?</h2>
              {selectedUsers.length > 0 && <span className={styles.stepCount}>{selectedUsers.length} SELECTED</span>}
            </div>
            <div className={styles.stepBody}>
              <div className={styles.inputBox} ref={dropdownRef}>
                {selectedUsers.map(su => (
                  <div key={su.id} className={styles.userChip}>
                    <Avatar src={su.avatar_url} name={`${su.first_name} ${su.last_name}`} size="xs" />
                    <span>{su.first_name} {su.last_name}</span>
                    <button className={styles.chipX} onClick={() => setSelectedUsers(prev => prev.filter(u => u.id !== su.id))}><X size={11} /></button>
                  </div>
                ))}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => { setShowDropdown(true); if (users.length === 0) fetchUsers(); }}
                  placeholder="Search a colleague or team..."
                  className={styles.inlineInput}
                />
                {showDropdown && (
                  <div className={styles.dropdown}>
                    {usersLoading ? <div className={styles.dropdownEmpty}>Loading...</div> :
                      filteredUsers.filter(u => !selectedUsers.some(su => su.id === u.id)).length === 0 ? <div className={styles.dropdownEmpty}>No results</div> :
                      filteredUsers.filter(u => !selectedUsers.some(su => su.id === u.id)).slice(0, 8).map(u => (
                        <div key={u.id} className={styles.dropdownItem} onClick={() => { setSelectedUsers(prev => [...prev, u]); setShowDropdown(false); setSearchQuery(''); }}>
                          <Avatar src={u.avatar_url} name={`${u.first_name} ${u.last_name}`} size="sm" />
                          <div>
                            <div className={styles.dropdownName}>{u.first_name} {u.last_name}</div>
                            {u.position && <div className={styles.dropdownRole}>{u.position}</div>}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              {suggestedUsers.length > 0 && selectedUsers.length === 0 && (
                <div className={styles.suggestedRow}>
                  <span className={styles.suggestedLabel}>SUGGESTED</span>
                  {suggestedUsers.map(u => (
                    <button key={u.id} className={styles.suggestedChip} onClick={() => setSelectedUsers(prev => [...prev, u])}>
                      <Avatar src={u.avatar_url} name={`${u.first_name} ${u.last_name}`} size="xs" />
                      <span>{u.first_name} {u.last_name?.charAt(0)} {u.last_name?.slice(1)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Type */}
          <div className={`${styles.stepCard} ${selectedType ? styles.stepDone : ''}`} style={{ borderLeftColor: '#e8a93c' }}>
            <div className={styles.stepHeader}>
              <span className={styles.stepNum} style={{ background: '#e8a93c' }}>{selectedType ? <Check size={13} /> : '2'}</span>
              <h2 className={styles.stepTitle}>What kind of ripple?</h2>
            </div>
            <div className={styles.stepBody}>
              <div className={styles.typeGrid}>
                {RIPPLE_TYPES.map(t => {
                  const sel = selectedType === t.id;
                  return (
                    <button key={t.id} className={`${styles.typeCard} ${sel ? styles.typeSelected : ''}`}
                      style={sel ? { background: t.bg, borderColor: t.color } : {}}
                      onClick={() => setSelectedType(sel ? null : t.id)}
                    >
                      <span className={styles.typeEmoji}>{t.emoji}</span>
                      <span className={styles.typeLabel} style={sel ? { color: t.color, fontWeight: 700 } : {}}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Step 3: Message */}
          <div className={`${styles.stepCard} ${displayLen >= 10 ? styles.stepDone : ''}`} style={{ borderLeftColor: '#0f7a68' }}>
            <div className={styles.stepHeader}>
              <span className={styles.stepNum} style={{ background: '#0f7a68' }}>{displayLen >= 10 ? <Check size={13} /> : '3'}</span>
              <h2 className={styles.stepTitle}>In your words</h2>
              <span className={styles.stepCount}>{displayLen} / 600</span>
            </div>
            <div className={styles.stepBody}>
              <div className={styles.messageBox}>
                <span className={styles.quoteDecor}>&ldquo;</span>
                <MentionInput
                  mode="textarea"
                  value={message}
                  onChange={(val) => setMessage(val)}
                  placeholder="Say what they did and why it mattered..."
                  maxLength={2000}
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Step 4: Media & Files */}
          <div className={styles.stepCardCompact}>
            <div className={styles.stepCardCompactHeader}>
              <span className={styles.stepNum} style={{ background: '#94a3b8' }}>4</span>
              <h2 className={styles.stepTitle}>Add media <span className={styles.optionalTag}>OPTIONAL</span></h2>
            </div>
            <input type="file" ref={fileInputRef} accept="image/*,video/*" multiple onChange={handleMediaSelect} className={styles.hiddenFileInput} />
            <input type="file" ref={fileDocInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" multiple onChange={handleMediaSelect} className={styles.hiddenFileInput} />
            {mediaFiles.length > 0 && (
              <div className={styles.mediaPreviewRow}>
                {mediaFiles.map((mf, i) => (
                  <div key={i} className={styles.mediaThumb}>
                    {mf.type === 'image' ? (
                      <img src={mf.url} alt="" />
                    ) : mf.type === 'video' ? (
                      <video src={mf.url} />
                    ) : (
                      <div className={styles.fileThumbLabel}><FileText size={18} /><span>{mf.name?.split('.').pop()?.toUpperCase()}</span></div>
                    )}
                    <button className={styles.mediaRemove} onClick={() => handleRemoveMedia(i)}><X size={10} /></button>
                  </div>
                ))}
              </div>
            )}
            {mediaFiles.length < 5 && (
              <div className={styles.uploadBtnRow}>
                <button className={styles.addMediaBtn} onClick={() => fileInputRef.current?.click()}>
                  <Image size={14} /> Upload media
                </button>
                <button className={styles.addMediaBtn} onClick={() => fileDocInputRef.current?.click()}>
                  <FileText size={14} /> Upload file
                </button>
              </div>
            )}
          </div>

          {/* Step 5: Values */}
          {values.length > 0 && (
            <div className={styles.stepCardCompact}>
              <div className={styles.stepCardCompactHeader}>
                <span className={styles.stepNum} style={{ background: '#94a3b8' }}>5</span>
                <h2 className={styles.stepTitle}>Company value <span className={styles.optionalTag}>OPTIONAL</span></h2>
              </div>
              <div className={styles.valueGrid}>
                {values.map(v => {
                  const sel = selectedValues.has(v.id);
                  return (
                    <button key={v.id} className={`${styles.valueChip} ${sel ? styles.valueSelected : ''}`} onClick={() => toggleValue(v.id)}>
                      {v.icon_url ? <img src={v.icon_url} alt="" className={styles.valueChipIcon} /> : renderValueIcon(v.icon, 14, sel ? '#0F6E56' : undefined)}
                      <span>{v.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Mobile-only: wave selector + submit */}
          <div className={styles.mobileControls}>
            <div className={styles.mobileWaveSection}>
              <span className={styles.mobileWaveLabel}>WAVES</span>
              <div className={styles.mobileWaveRow}>
                {WAVE_PRESETS.map(w => (
                  <button key={w} className={`${styles.mobileWavePill} ${customPoints === w && !isCustomWave ? styles.mobileWavePillActive : ''}`}
                    onClick={() => { setCustomPoints(w); setIsCustomWave(false); }}>{w}</button>
                ))}
              </div>
              <input
                type="number" min={5} max={1000} placeholder="Custom amount"
                value={isCustomWave ? customPoints : ''}
                onChange={(e) => { const v = Math.min(1000, Math.max(5, parseInt(e.target.value) || 5)); setCustomPoints(v); setIsCustomWave(true); }}
                className={styles.mobileCustomInput}
              />
            </div>
            {totalWaveCost > 0 && (
              <div className={styles.mobileCostRow}>
                <span>Cost: {totalWaveCost} waves</span>
                <span style={{ color: afterBalance < 0 ? '#DC2626' : '#0f7a68' }}>After: {afterBalance}</span>
              </div>
            )}
            {error && <div className={styles.formError}>{error}</div>}
            <button className={`${styles.submitBtn} ${canSend ? '' : styles.submitDisabled}`} onClick={handleSubmit} disabled={!canSend || loading}>
              {loading ? 'Sending...' : 'Send ripple'}
            </button>
            {!canSend && <p className={styles.submitHint}>{selectedUsers.length === 0 ? 'Select a colleague' : !selectedType ? 'Choose a type' : displayLen < 10 ? 'Write at least 10 characters' : afterBalance < 0 ? 'Not enough waves' : ''}</p>}
          </div>
        </div>

        {/* ═══ RIGHT: PREVIEW + CONTROLS ═══ */}
        <div className={styles.sidebar}>
          <div className={styles.previewLabel}>
            <span className={styles.previewDot} /> LIVE PREVIEW
          </div>

          <div className={styles.previewCard}>
            <div className={styles.previewTop}>
              <div className={styles.previewTypeCol}>
                {typeObj ? (
                  <>
                    <div className={styles.previewTypeIcon} style={{ background: typeObj.color }} />
                    <span className={styles.previewTypeName}>{typeObj.label.toUpperCase()}</span>
                    <span className={styles.previewWavesBig}>{waveCostPerPerson}</span>
                    <span className={styles.previewWavesLabel}>waves each</span>
                  </>
                ) : (
                  <span className={styles.previewWavesLabel}>Select a type</span>
                )}
              </div>
              <div className={styles.previewInfoCol}>
                <div className={styles.previewSender}>
                  <Avatar name={`${user?.first_name} ${user?.last_name}`} src={user?.avatar_url} size="md" />
                  <div>
                    <span className={styles.previewSenderName}>{user?.first_name} {user?.last_name}</span>
                    {selectedUsers.length > 0 && (
                      <div className={styles.previewRecipients}>
                        to {selectedUsers.map(u => u.first_name).join(' & ')}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.previewMsg}>
                  {message ? <MentionText text={message} /> : 'Your message will appear here...'}
                </div>
              </div>
            </div>
          </div>

          {/* Wave selector */}
          <div className={styles.waveCard}>
            <span className={styles.waveCardLabel}>WAVES EACH</span>
            <div className={styles.waveRow}>
              {WAVE_PRESETS.map(w => (
                <button key={w} className={`${styles.wavePill} ${customPoints === w && !isCustomWave ? styles.wavePillActive : ''}`}
                  onClick={() => { setCustomPoints(w); setIsCustomWave(false); }}>{w}</button>
              ))}
            </div>
            <div className={styles.customWaveRow}>
              <input
                type="number"
                min={5}
                max={1000}
                placeholder="Custom"
                value={isCustomWave ? customPoints : ''}
                onChange={(e) => { const v = Math.min(1000, Math.max(5, parseInt(e.target.value) || 5)); setCustomPoints(v); setIsCustomWave(true); }}
                className={styles.customWaveInput}
              />
            </div>
            {totalWaveCost > 0 && (
              <div className={styles.waveSummary}>
                <div className={styles.waveSummaryRow}>
                  <span>This ripple costs</span>
                  <span className={styles.waveSummaryBold}>{totalWaveCost} waves</span>
                </div>
                <div className={styles.waveSummaryRow}>
                  <span>Left to give after</span>
                  <span className={styles.waveSummaryBold} style={{ color: afterBalance < 0 ? '#DC2626' : undefined }}>{afterBalance} waves</span>
                </div>
              </div>
            )}
          </div>

          {error && <div className={styles.formError}>{error}</div>}

          <button className={`${styles.submitBtn} ${canSend ? '' : styles.submitDisabled}`} onClick={handleSubmit} disabled={!canSend || loading}>
            {loading ? 'Sending...' : 'Send ripple'}
          </button>

          <p className={styles.submitHint}>
            {!canSend ? (selectedUsers.length === 0 ? 'Select a colleague to continue' : !selectedType ? 'Choose a ripple type' : displayLen < 10 ? 'Write at least 10 characters' : afterBalance < 0 ? 'Not enough waves' : '') : 'It posts to the feed and notifies both people.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Appreciate;
